import { useState, type ReactNode } from "react";
import { X, AlertCircle } from "lucide-react";

export function ConfirmModal({
  open,
  title,
  body,
  motivoLabel = "Motivo",
  motivoRequired = false,
  confirmLabel = "Confirmar",
  confirmTone = "primary",
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  motivoLabel?: string;
  motivoRequired?: boolean;
  confirmLabel?: string;
  confirmTone?: "primary" | "danger";
  onCancel: () => void;
  /** Devuelve la promesa de la acción. El modal queda en loading hasta resolver. */
  onConfirm: (motivo: string) => Promise<void>;
}) {
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleConfirm = async () => {
    if (motivoRequired && !motivo.trim()) {
      setError("El motivo es obligatorio.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onConfirm(motivo.trim());
      setMotivo("");
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "Error inesperado.";
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    setMotivo("");
    setError(null);
    onCancel();
  };

  const confirmBtnCls =
    confirmTone === "danger"
      ? "bg-action-err hover:bg-action-err-hover text-white"
      : "bg-primary hover:opacity-90 text-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-surface shadow-overlay">
        <div className="flex items-start justify-between border-b px-5 py-3">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button
            onClick={handleCancel}
            className="rounded p-1 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          {body && <div className="text-sm text-fg-muted">{body}</div>}
          <div>
            <label className="text-xs uppercase tracking-wide text-fg-subtle">
              {motivoLabel} {motivoRequired && <span className="text-estado-err">*</span>}
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-edge-strong px-2 py-1 text-sm focus:border-primary focus:outline-none"
              placeholder={motivoRequired ? "Obligatorio" : "Opcional"}
              disabled={loading}
            />
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button
            onClick={handleCancel}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            className={`rounded px-3 py-1.5 text-sm ${confirmBtnCls} disabled:opacity-50`}
            disabled={loading}
          >
            {loading ? "Procesando…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
